// ─────────────────────────────────────────────────────────────────────────────
//  LeadTemplatesAdmin — manage the standard Scope + BOM templates that drive the
//  lead "Suggest scope / BOM" feature. One template per project type (sub-group).
//
//  Master-detail: a list of templates on the left; the selected template's header,
//  scope lines and BOM lines (with basis rules) editable on the right. Everything
//  is add / edit / delete. Reached at /officeuse/lead-admin (OFFICE_USE gated).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from "react";
import { Plus, Save, Trash2, RefreshCw } from "lucide-react";
import api from "../services/leadsapi.js";
import useToast from "../hooks/useToast";
import ToastContainer from "../components/Notification_Toast/ToastContainer.js";
import ConfirmationModal from "../components/ConfirmationModal.js";
import useConfirmationModal from "../components/HandleConfirmationModal.js";
import UnitSelectCell from "../components/Dropdowns/UnitSelectCell.js";
import { BASIS_OPTIONS, SITE_VISIT_FIELDS } from "../constants/scopeActivities.js";
import "../pages-css/LeadTemplatesAdmin.css";

const blankScope = () => ({ id: null, activity: "", category: "", specification: "", unit: "", notes: "" });
const blankBom = () => ({
  id: null, scopeActivity: "", category: "", itemName: "", make: "", specification: "",
  unit: "", basis: "PER_KW", basisValue: "", stepValue: "", siteVisitField: "", defaultUnitRate: "", notes: "",
});

export default function LeadTemplatesAdmin() {
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const { confirmModal, showConfirmation } = useConfirmationModal();

  const [templates, setTemplates] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // {id, projectType, name, description, isActive}
  const [scopeLines, setScopeLines] = useState([]);
  const [bomLines, setBomLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingScope, setSavingScope] = useState(false);
  const [savingBom, setSavingBom] = useState(false);

  // New-template form
  const [newType, setNewType] = useState("");
  const [newName, setNewName] = useState("");

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get("/admin/lead-templates");
      setTemplates(Array.isArray(res) ? res : []);
    } catch (e) {
      if (e.message !== "SESSION_EXPIRED") showError("Failed to load templates");
    }
  }, [showError]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadTemplates();
      try {
        const res = await api.get("/admin/dropdowns/subgroups");
        const list = Array.isArray(res) ? res : (Array.isArray(res?.content) ? res.content : []);
        setSubGroups(list.map(s => s.subGroupName).filter(Boolean));
      } catch { /* non-fatal */ }
      setLoading(false);
    })();
  }, [loadTemplates]);

  const openTemplate = async (id) => {
    setSelectedId(id);
    try {
      const t = await api.get(`/admin/lead-templates/${id}`);
      setDetail({ id: t.id, projectType: t.projectType, name: t.name || "", description: t.description || "", isActive: t.isActive });
      setScopeLines((t.scopeItems || []).map(s => ({
        id: s.id, activity: s.activity || "", category: s.category || "",
        specification: s.specification || "", unit: s.unit || "", notes: s.notes || "",
      })));
      setBomLines((t.bomItems || []).map(b => ({
        id: b.id, scopeActivity: b.scopeActivity || "", category: b.category || "", itemName: b.itemName || "",
        make: b.make || "", specification: b.specification || "", unit: b.unit || "",
        basis: b.basis || "PER_KW", basisValue: b.basisValue ?? "", stepValue: b.stepValue ?? "",
        siteVisitField: b.siteVisitField || "", defaultUnitRate: b.defaultUnitRate ?? "", notes: b.notes || "",
      })));
    } catch (e) {
      if (e.message !== "SESSION_EXPIRED") showError("Failed to open template");
    }
  };

  const createTemplate = async () => {
    if (!newType) { showError("Pick a project type"); return; }
    if (templates.some(t => t.projectType === newType && t.isActive)) {
      showError("An active template already exists for that project type"); return;
    }
    try {
      const t = await api.post("/admin/lead-templates", { projectType: newType, name: newName.trim() || null, isActive: true });
      setNewType(""); setNewName("");
      await loadTemplates();
      showSuccess("Template created");
      openTemplate(t.id);
    } catch (e) { if (e.message !== "SESSION_EXPIRED") showError("Failed to create template"); }
  };

  const saveHeader = async () => {
    if (!detail) return;
    try {
      await api.put(`/admin/lead-templates/${detail.id}`, {
        projectType: detail.projectType, name: detail.name.trim() || null,
        description: detail.description.trim() || null, isActive: detail.isActive,
      });
      await loadTemplates();
      showSuccess("Template saved");
    } catch (e) { if (e.message !== "SESSION_EXPIRED") showError("Failed to save template"); }
  };

  const deleteTemplate = async (t) => {
    const ok = await showConfirmation({
      title: t.isActive ? "Deactivate template" : "Delete template permanently",
      type: "alert",
      message: t.isActive
        ? `Deactivate the ${t.projectType} template? It stops being suggested but is kept.`
        : `Permanently delete the ${t.projectType} template and all its lines? This cannot be undone.`,
      confirmText: t.isActive ? "Deactivate" : "Delete", cancelText: "Cancel",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/lead-templates/${t.id}`);
      if (selectedId === t.id) { setSelectedId(null); setDetail(null); setScopeLines([]); setBomLines([]); }
      await loadTemplates();
      showSuccess(t.isActive ? "Template deactivated" : "Template deleted");
    } catch (e) { if (e.message !== "SESSION_EXPIRED") showError("Failed to delete template"); }
  };

  // ── Scope lines ──
  const updScope = (i, k, v) => setScopeLines(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const saveScope = async () => {
    for (const r of scopeLines) if (!r.activity.trim()) { showError("Every scope line needs an activity"); return; }
    setSavingScope(true);
    try {
      await api.put(`/admin/lead-templates/${detail.id}/scope-items`, {
        items: scopeLines.map((r, i) => ({
          id: r.id ?? null, seqNo: i + 1, activity: r.activity.trim(),
          category: r.category || null, specification: r.specification || null,
          unit: r.unit || null, notes: r.notes || null,
        })),
      });
      await openTemplate(detail.id);
      showSuccess("Scope lines saved");
    } catch (e) { if (e.message !== "SESSION_EXPIRED") showError("Failed to save scope lines"); }
    finally { setSavingScope(false); }
  };

  // ── BOM lines ──
  const updBom = (i, k, v) => setBomLines(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const saveBom = async () => {
    for (const r of bomLines) if (!r.itemName.trim()) { showError("Every BOM line needs an item name"); return; }
    setSavingBom(true);
    try {
      await api.put(`/admin/lead-templates/${detail.id}/bom-items`, {
        lines: bomLines.map((r, i) => ({
          id: r.id ?? null, seqNo: i + 1, scopeActivity: r.scopeActivity || null,
          category: r.category || null, itemName: r.itemName.trim(), make: r.make || null,
          specification: r.specification || null, unit: r.unit || null, basis: r.basis || "PER_KW",
          basisValue: r.basisValue === "" ? null : Number(r.basisValue),
          stepValue: r.stepValue === "" ? null : Number(r.stepValue),
          siteVisitField: r.siteVisitField || null,
          defaultUnitRate: r.defaultUnitRate === "" ? null : Number(r.defaultUnitRate),
          notes: r.notes || null,
        })),
      });
      await openTemplate(detail.id);
      showSuccess("BOM lines saved");
    } catch (e) { if (e.message !== "SESSION_EXPIRED") showError("Failed to save BOM lines"); }
    finally { setSavingBom(false); }
  };

  // Which extra input a basis needs (value / step / site-visit field).
  const basisInput = (r, i) => {
    if (r.basis === "PER_STEP") {
      return <input className="lta-inp" type="number" min="0" step="any" value={r.stepValue}
        placeholder="kW / unit" onChange={e => updBom(i, "stepValue", e.target.value)} />;
    }
    if (r.basis === "FROM_SITE_VISIT") {
      return (
        <select className="lta-inp" value={r.siteVisitField} onChange={e => updBom(i, "siteVisitField", e.target.value)}>
          <option value="">— field —</option>
          {SITE_VISIT_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      );
    }
    // FIXED or PER_KW → a numeric value
    return <input className="lta-inp" type="number" min="0" step="any" value={r.basisValue}
      placeholder={r.basis === "PER_KW" ? "per kW" : "qty"} onChange={e => updBom(i, "basisValue", e.target.value)} />;
  };

  if (loading) return <div className="lta-page"><div className="lta-loading">Loading templates…</div></div>;

  const scopeActivityOptions = scopeLines.map(s => s.activity).filter(Boolean);

  return (
    <div className="lta-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <ConfirmationModal {...confirmModal} />

      <div className="lta-head">
        <h1 className="lta-title">Lead Scope / BOM Templates</h1>
        <p className="lta-sub">Standard scope and materials per project type — used to suggest a starting estimate for a lead.</p>
      </div>

      <div className="lta-layout">
        {/* ── Left: template list + create ── */}
        <div className="lta-list-card">
          <div className="lta-list-head">
            <span>Templates</span>
            <button className="lta-icon" title="Refresh" onClick={loadTemplates}><RefreshCw size={14} /></button>
          </div>

          <div className="lta-create">
            <select className="lta-inp" value={newType} onChange={e => setNewType(e.target.value)}>
              <option value="">Project type…</option>
              {subGroups.map(sg => <option key={sg} value={sg}>{sg}</option>)}
            </select>
            <input className="lta-inp" placeholder="Name (optional)" value={newName} onChange={e => setNewName(e.target.value)} />
            <button className="lta-btn-primary" onClick={createTemplate}><Plus size={13} /> Add</button>
          </div>

          <div className="lta-list">
            {templates.length === 0 && <div className="lta-empty">No templates yet.</div>}
            {templates.map(t => (
              <div key={t.id} className={`lta-list-item${selectedId === t.id ? " active" : ""}`} onClick={() => openTemplate(t.id)}>
                <div className="lta-list-main">
                  <b>{t.projectType}</b>
                  {t.name && <span className="lta-list-name">{t.name}</span>}
                </div>
                {!t.isActive && <span className="lta-badge-off">inactive</span>}
                <button className="lta-icon-del" title={t.isActive ? "Deactivate" : "Delete"}
                  onClick={e => { e.stopPropagation(); deleteTemplate(t); }}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: selected template detail ── */}
        <div className="lta-detail">
          {!detail ? (
            <div className="lta-detail-empty">Select a template on the left, or create one.</div>
          ) : (
            <>
              <div className="lta-card">
                <div className="lta-card-head"><h4>Template</h4></div>
                <div className="lta-form-grid">
                  <label className="lta-field"><span>Project Type</span><input className="lta-inp" value={detail.projectType} disabled /></label>
                  <label className="lta-field"><span>Name</span>
                    <input className="lta-inp" value={detail.name} onChange={e => setDetail(d => ({ ...d, name: e.target.value }))} /></label>
                  <label className="lta-field"><span>Active</span>
                    <select className="lta-inp" value={detail.isActive ? "1" : "0"}
                      onChange={e => setDetail(d => ({ ...d, isActive: e.target.value === "1" }))}>
                      <option value="1">Active</option><option value="0">Inactive</option>
                    </select></label>
                  <label className="lta-field lta-field--full"><span>Description</span>
                    <input className="lta-inp" value={detail.description} onChange={e => setDetail(d => ({ ...d, description: e.target.value }))} /></label>
                </div>
                <div className="lta-card-foot"><button className="lta-btn-primary" onClick={saveHeader}><Save size={13} /> Save template</button></div>
              </div>

              {/* Scope lines */}
              <div className="lta-card">
                <div className="lta-card-head">
                  <h4>Scope Lines</h4>
                  <button className="lta-btn-ghost" onClick={() => setScopeLines(p => [...p, blankScope()])}><Plus size={13} /> Add line</button>
                </div>
                <div className="lta-table-wrap">
                  <table className="lta-table">
                    <thead><tr><th>#</th><th>Activity</th><th>Category</th><th>Specification</th><th>Unit</th><th>Notes</th><th /></tr></thead>
                    <tbody>
                      {scopeLines.length === 0 && <tr><td colSpan={7} className="lta-empty">No scope lines.</td></tr>}
                      {scopeLines.map((r, i) => (
                        <tr key={r.id ?? `n${i}`}>
                          <td>{i + 1}</td>
                          <td><input className="lta-inp" value={r.activity} onChange={e => updScope(i, "activity", e.target.value)} placeholder="Activity" /></td>
                          <td><input className="lta-inp" value={r.category} onChange={e => updScope(i, "category", e.target.value)} /></td>
                          <td><input className="lta-inp" value={r.specification} onChange={e => updScope(i, "specification", e.target.value)} /></td>
                          <td className="lta-c-unit"><UnitSelectCell className="lta-inp" value={r.unit} onChange={v => updScope(i, "unit", v)} /></td>
                          <td><input className="lta-inp" value={r.notes} onChange={e => updScope(i, "notes", e.target.value)} /></td>
                          <td><button className="lta-icon-del" onClick={() => setScopeLines(p => p.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="lta-card-foot"><button className="lta-btn-primary" onClick={saveScope} disabled={savingScope}><Save size={13} /> {savingScope ? "Saving…" : "Save scope lines"}</button></div>
              </div>

              {/* BOM lines */}
              <div className="lta-card">
                <div className="lta-card-head">
                  <h4>BOM Lines</h4>
                  <button className="lta-btn-ghost" onClick={() => setBomLines(p => [...p, blankBom()])}><Plus size={13} /> Add line</button>
                </div>
                <div className="lta-table-wrap">
                  <table className="lta-table lta-table--bom">
                    <thead><tr>
                      <th>#</th><th>For Activity</th><th>Category</th><th>Item</th><th>Make</th>
                      <th>Unit</th><th>Basis</th><th>Value / Step</th><th>Rate</th><th>Notes</th><th />
                    </tr></thead>
                    <tbody>
                      {bomLines.length === 0 && <tr><td colSpan={11} className="lta-empty">No BOM lines.</td></tr>}
                      {bomLines.map((r, i) => (
                        <tr key={r.id ?? `n${i}`}>
                          <td>{i + 1}</td>
                          <td>
                            <select className="lta-inp" value={r.scopeActivity} onChange={e => updBom(i, "scopeActivity", e.target.value)}>
                              <option value="">— General —</option>
                              {!scopeActivityOptions.includes(r.scopeActivity) && r.scopeActivity && <option value={r.scopeActivity}>{r.scopeActivity}</option>}
                              {scopeActivityOptions.map(a => <option key={a} value={a}>{a}</option>)}
                            </select>
                          </td>
                          <td><input className="lta-inp" value={r.category} onChange={e => updBom(i, "category", e.target.value)} /></td>
                          <td><input className="lta-inp" value={r.itemName} onChange={e => updBom(i, "itemName", e.target.value)} placeholder="Item name" /></td>
                          <td><input className="lta-inp" value={r.make} onChange={e => updBom(i, "make", e.target.value)} /></td>
                          <td className="lta-c-unit"><UnitSelectCell className="lta-inp" value={r.unit} onChange={v => updBom(i, "unit", v)} /></td>
                          <td>
                            <select className="lta-inp" value={r.basis} onChange={e => updBom(i, "basis", e.target.value)}
                              title={(BASIS_OPTIONS.find(b => b.value === r.basis) || {}).help}>
                              {BASIS_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                            </select>
                          </td>
                          <td>{basisInput(r, i)}</td>
                          <td><input className="lta-inp" type="number" min="0" step="any" value={r.defaultUnitRate} onChange={e => updBom(i, "defaultUnitRate", e.target.value)} placeholder="opt." /></td>
                          <td><input className="lta-inp" value={r.notes} onChange={e => updBom(i, "notes", e.target.value)} /></td>
                          <td><button className="lta-icon-del" onClick={() => setBomLines(p => p.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="lta-card-foot"><button className="lta-btn-primary" onClick={saveBom} disabled={savingBom}><Save size={13} /> {savingBom ? "Saving…" : "Save BOM lines"}</button></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
